import { IModelPortfolioSubscription } from '../../models/plannedPortfolios';
import { EventEmitter, Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ICalculateModelPortfolioDriftRequest, ICreateRebalancesRequest, IModelPortfolio, IModelPortfolioSubscriptions, IPlannedPortfolioExportRequest } from '../../models/modelPortfolios';
import { BaseHttpService } from './base.http.service';
import { AppConfig } from 'src/app/app.config';
import { IEnvironmentConfig } from 'src/app/models/environmentConfig';
import { AssetAllocation } from "../../models/assetAllocation";

export interface IPagedModelPortfolioSubscriptions {
  items: IModelPortfolioSubscription[];
  count: number;
}

@Injectable()
export class ModelPortfoliosService extends BaseHttpService {
    onOrderCreated = new EventEmitter<number>()
    onCloseModalWindowPositions = new EventEmitter<boolean>()
    constructor(private http: HttpClient, private appConfig: AppConfig) {
        super();
    }

    get envSettings(): IEnvironmentConfig {
        return this.appConfig.getEnvironmentConfig();
    }

    public submitModelPortfolio(modelPortfolio: IModelPortfolio): Observable<any> {
        const jsonBody = JSON.stringify(modelPortfolio);
        const url = `${this.envSettings.webApiUrl}modelPortfolios`;
        return this.http.post(url, jsonBody, {
            responseType: 'json',
            headers: this.getHttpHeaders()
        })
    }

    public submitModelPortfolioSubscriptions(modelPortfolio: IModelPortfolioSubscriptions): Observable<any> {
        const jsonBody = JSON.stringify(modelPortfolio.portfolioIds);
        const url = `${this.envSettings.webApiUrl}modelportfolios/${modelPortfolio.modelPortfolioId}/subscriptions`;
        return this.http.post(url, jsonBody, {
            responseType: 'json',
            headers: this.getHttpHeaders()
        });
    }

    /**
     * Deletes a subscription associated with a specific model portfolio.
     */
    public deleteSubscription(modelPortfolioId: number, subscriptionId: number): Observable<any> {
        const url = `${this.envSettings.webApiUrl}modelportfolios/${modelPortfolioId}/subscriptions/${subscriptionId}`;
        return this.http.delete(url, {
            responseType: 'json',
            headers: this.getHttpHeaders()
        });
    }

    public deleteModelPortfolio(modelPortfolioId: number): Observable<any> {
        const url = `${this.envSettings.webApiUrl}modelportfolios/${modelPortfolioId}`;
        return this.http.delete(url, {
            observe: 'body',
            responseType: 'json',
            headers: this.getHttpHeaders()
        });
    }

    public getModelPortfolios(): Observable<any> {
        const url = `${this.envSettings.webApiUrl}modelPortfolios?includeSubscriptions=true`;
        return this.http.get(url, {
            observe: 'body',
            responseType: 'json',
            headers: this.getHttpHeaders()
        });
    }

    public getAssetAllocations(): Observable<AssetAllocation[]> {
      const url = `${this.envSettings.webApiUrl}admin/assetAllocation`;
      return this.http.get<AssetAllocation[]>(url, {
          observe: 'body',
          responseType: 'json',
          headers: this.getHttpHeaders()
      });
    }

    public getModelPortfoliosSubscriptions(modelPortfolioId: number): Observable<IModelPortfolioSubscription[]> {
        const url = `${this.envSettings.webApiUrl}modelportfolios/${modelPortfolioId}/subscriptions?includeRebalances=true&includeDrift=true`;

        return this.http.get<IModelPortfolioSubscription[]>(url, {
            observe: 'body',
            responseType: 'json',
            headers: this.getHttpHeaders()
        });
    }

    public getAllModelPortfoliosSubscriptions(
      pageNumber: number = 0,
      pageSize: number = 30,
      investorNameSearch: string = null,
      rebalanceCreatedAt: Date = null,
      minimumDrift: number = null,
      cashToleranceCheck: boolean = false,
      includeNonSubscribedPortfolios: boolean = false,
        ): Observable<IPagedModelPortfolioSubscriptions> {
            const url = new URL(`${this.envSettings.webApiUrl}modelportfolios/subscriptions`);

            url.searchParams.append('pageNumber', pageNumber.toString());
            url.searchParams.append('pageSize', pageSize.toString());

            if (investorNameSearch) {
              url.searchParams.append('investorNameSearch', investorNameSearch);
            }
            if (rebalanceCreatedAt) {
              url.searchParams.append('rebalanceCreatedAt', rebalanceCreatedAt.toISOString());
            }
            if (minimumDrift) {
              url.searchParams.append('minimumDrift', minimumDrift.toString());
            }
            if (cashToleranceCheck) {
                url.searchParams.append('cashToleranceCheck', cashToleranceCheck.toString());
              }
            if (includeNonSubscribedPortfolios) {
              url.searchParams.append('includeNonSubscribedPortfolios', includeNonSubscribedPortfolios.toString());
            }

            return this.http.get<IPagedModelPortfolioSubscriptions>(url.toString(), {
                observe: 'body',
                responseType: 'json',
                headers: this.getHttpHeaders()
            });
    }

    public getModelPortfoliosForPortfolios(portfolioIds: number[]): Observable<IModelPortfolio[]> {
        const url = new URL(`${this.envSettings.webApiUrl}portfolios/modelPortfolios`);

        portfolioIds.forEach(portfolioId => {
          url.searchParams.append('portfolioIds', portfolioId.toString());
        });

        return this.http.get<IModelPortfolio[]>(url.toString(), {
            observe: 'body',
            responseType: 'json',
            headers: this.getHttpHeaders()
        });
    }

    /**
     * Apply model portfolios for subscriptions.
     * This will generate and return planned portfolios for the given subscriptions.
     */
    public applyModelPortfoliosForSubscriptions(subscriptionIds: number[]): Observable<any> {
      const url = `${this.envSettings.webApiUrl}modelportfolios/subscriptions/applymodel`;

      return this.http.post(url, subscriptionIds, {
        observe: 'body',
        responseType: 'json',
        headers: this.getHttpHeaders()
      });
    }

  /**
   * Generates planned portfolios for the given subscribers in the payload.
   */
  public generatePlannedPositions(payload: IPlannedPortfolioExportRequest): Observable<any> {
      let postOptions;
      if (payload.exportAsExcel) {
          postOptions = {
            responseType: 'blob',
            headers: this.getHttpHeaders()
          }
        } else {
          postOptions = {
            responseType: 'json',
            headers: this.getHttpHeaders()
          }
        }

        const url = `${this.envSettings.webApiUrl}modelportfolios/export`;
        return this.http.post(url, payload, postOptions)
    };

    public uploadRebalanceFile(file: File): Observable<HttpResponse<any>> {
        const formData = new FormData();

        formData.append('file', file, file.name);

        const url = `${this.envSettings.webApiUrl}modelportfolios/rebalances/upload`;
        return this.http.post(url, formData, {
            headers: this.getHttpHeadersWithoutContentType(),
            observe: 'response'
        });
    };

    /**
     * Create rebalances instructions with prescribed buy/sell ref values which can be overridden by the user.
     */
    public createPrescribedValuesRebalanceModel(payload: any): Observable<any> {
          const requestObject = {
              portfolioId: payload.portfolioId,
              sourceModelPortfolioId: payload.sourceModelPortfolioId,
              positions: payload.positions,
              rebalanceReduceDriftType: payload.rebalanceReduceDriftType,
              ignoreTolerance: payload.ignoreTolerance,
              withdrawalAmount: payload.withdrawalAmount,
              withdrawalCurrency: payload.withdrawalCurrency
          };
          const jsonBody = JSON.stringify(requestObject);
          const url = `${this.envSettings.webApiUrl}modelportfolios/rebalances/create-prescribed-values`;
          return this.http.post(url, jsonBody, {
              responseType: 'json',
              headers: this.getHttpHeaders()
          })
      }

    /**
    * Create rebalances instructions for selected subscriptions
    */
    public createRebalancesForSubscriptions(payload: ICreateRebalancesRequest): Observable<any> {
        let url = `${this.envSettings.webApiUrl}modelportfolios/rebalances/create`;

        return this.http.post(url, payload, {
          headers: this.getHttpHeaders()
        });
    }

    /**
     * Calculate model portfolio drift for the given payload.
     * This will fire a hangfire job to process asynchronously.
     * @param payload
     * @return number - Number of portfolios to calculate drift for
     */
    public calculateModelPortfolioDrift(payload: ICalculateModelPortfolioDriftRequest): Observable<number> {
        let url = `${this.envSettings.webApiUrl}modelportfolios/drift/calculate`;

        return this.http.post<number>(url, payload, {
          headers: this.getHttpHeaders()
        });
    }

    /**
     * Polls to see how many portfolio drift calculations are remaining.
     *
     * @return {Observable<number>} An observable that emits the count of remaining portfolio drift calculations remaining.
     */
    public pollCalculateModelPortfolioDriftCount(): Observable<number> {
        let url = `${this.envSettings.webApiUrl}modelportfolios/drift/calculate/poll`;

        return this.http.get<number>(url, {
          headers: this.getHttpHeaders()
        });
    }
}
